import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';
import {
	CPCreqHistoryAdditionalAgreementSharedLocNames,
	creqAdditionalAgreementSharedLocs,
} from '../../models/additional-agreement.locs';

type CPCReqHistoryAdditionalAgreementDetailLocNames =
	| 'CPCReqHistoryAdditionalAgreementDetailsPageTitle'
	| 'CPCReqHistoryAdditionalAgreementDetailsPageDescription'
	| 'CPCReqHistoryAdditionalAgreementDetailsButtonBack'
	| 'CPCReqHistoryAdditionalAgreementDetailsButtonCancel'
	| 'CPCReqHistoryAdditionalAgreementDetailsButtonEdit'
	| 'CPCReqHistoryAdditionalAgreementDetailsButtonInfo'
	| 'CPCReqHistoryAdditionalAgreementDetailsButtonUpdate'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocLanguageInvalid'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocLanguageRequired'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocumentationLimit'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocumentationRequired'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocumentationSize'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelActiveDate'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelDataProvider'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelDocumentation'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelDocumentationLanguage'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelExpireDate'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelInvalid'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelNotes'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelProvider'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelReviewState'
	| 'CPCReqHistoryAdditionalAgreementDetailsFieldLabelUpdated'
	| 'CPCReqHistoryAdditionalAgreementDetailsNotificationMessageUpdateSuccess'
	| CPCreqHistoryAdditionalAgreementSharedLocNames;

export const creqAdditionalAgreementDetailLocs: LocDefs<CPCReqHistoryAdditionalAgreementDetailLocNames> =
	{
		CPCReqHistoryAdditionalAgreementDetailsPageTitle: {
			defaultValue: 'Additional Agreement Details',
			description: 'Page title for: Additional Agreement Details.',
			displayPath: 'History|Additional Agreement|Details|Title',
			portal: 'CP',
			displayType: 'Title',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.Title.PageTitle',
		},
		CPCReqHistoryAdditionalAgreementDetailsPageDescription: {
			defaultValue: '',
			description: 'Page description for: Additional Agreement Details.',
			displayPath: 'History|Additional Agreement|Details|Description',
			portal: 'CP',
			displayType: 'Content',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: true,
			shared: false,
		},

		CPCReqHistoryAdditionalAgreementDetailsButtonBack: {
			defaultValue: 'Back',
			description: 'Button label for: Back.',
			displayPath: 'History|Additional Agreement|Details|Back',
			portal: 'CP',
			displayType: 'Button',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.Button.Back',
		},
		CPCReqHistoryAdditionalAgreementDetailsButtonCancel: {
			defaultValue: 'Cancel',
			description: 'Button label for: Cancel.',
			displayPath: 'History|Additional Agreement|Details|Cancel',
			portal: 'CP',
			displayType: 'Button',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.Button.Cancel',
		},
		CPCReqHistoryAdditionalAgreementDetailsButtonEdit: {
			defaultValue: 'Edit',
			description: 'Button label for: Edit.',
			displayPath: 'History|Additional Agreement|Details|Edit',
			portal: 'CP',
			displayType: 'Button',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.Button.Edit',
		},
		CPCReqHistoryAdditionalAgreementDetailsButtonInfo: {
			defaultValue: 'Info',
			description: 'Button label for: Info.',
			displayPath: 'History|Additional Agreement|Details|Info',
			portal: 'CP',
			displayType: 'Button',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.Button.Info',
		},
		CPCReqHistoryAdditionalAgreementDetailsButtonUpdate: {
			defaultValue: 'Update',
			description: 'Button label for: Update.',
			displayPath: 'History|Additional Agreement|Details|Update',
			portal: 'CP',
			displayType: 'Button',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.Button.Update',
		},

		CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocLanguageInvalid: {
			defaultValue: 'Invalid documentation language',
			description: 'Error message for invalid documentation language.',
			displayPath: 'History|Additional Agreement|Details|DocLanguageInvalid',
			portal: 'CP',
			displayType: 'Error',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldError.DocLanguageInvalid',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocLanguageRequired: {
			defaultValue: 'Documentation language is required',
			description: 'Error message for required documentation language.',
			displayPath: 'History|Additional Agreement|Details|DocLanguageRequired',
			portal: 'CP',
			displayType: 'Error',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldError.DocLanguageRequired',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocumentationLimit: {
			defaultValue: 'Documentation limit exceeded',
			description: 'Error message for documentation limit.',
			displayPath: 'History|Additional Agreement|Details|DocLimit',
			portal: 'CP',
			displayType: 'Error',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldError.DocumentationLimit',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocumentationRequired: {
			defaultValue: 'Documentation is required',
			description: 'Error message for required documentation.',
			displayPath: 'History|Additional Agreement|Details|DocRequired',
			portal: 'CP',
			displayType: 'Error',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldError.DocumentationRequired',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldErrorDocumentationSize: {
			defaultValue: 'Documentation size exceeds limit',
			description: 'Error message for documentation size.',
			displayPath: 'History|Additional Agreement|Details|DocSize',
			portal: 'CP',
			displayType: 'Error',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldError.DocumentationSize',
		},

		CPCReqHistoryAdditionalAgreementDetailsFieldLabelActiveDate: {
			defaultValue: 'Active Date',
			description: 'Field label for: Active Date.',
			displayPath: 'History|Additional Agreement|Details|ActiveDate',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.ActiveDate',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldLabelDataProvider: {
			defaultValue: 'Data Provider',
			description: 'Field label for: Data Provider.',
			displayPath: 'History|Additional Agreement|Details|DataProvider',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.DataProvider',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldLabelDocumentation: {
			defaultValue: 'Documentation',
			description: 'Field label for: Documentation.',
			displayPath: 'History|Additional Agreement|Details|Documentation',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.Documentation',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldLabelDocumentationLanguage: {
			defaultValue: 'Documentation Language',
			description: 'Field label for: Documentation Language.',
			displayPath: 'History|Additional Agreement|Details|DocLanguage',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.DocumentationLanguage',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldLabelExpireDate: {
			defaultValue: 'Expire Date',
			description: 'Field label for: Expire Date.',
			displayPath: 'History|Additional Agreement|Details|ExpireDate',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.ExpireDate',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldLabelInvalid: {
			defaultValue: 'Invalid',
			description: 'Field label for: Invalid.',
			displayPath: 'History|Additional Agreement|Details|Invalid',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.Invalid',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldLabelNotes: {
			defaultValue: 'Notes',
			description: 'Field label for: Notes.',
			displayPath: 'History|Additional Agreement|Details|Notes',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.Notes',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldLabelProvider: {
			defaultValue: 'Provider',
			description: 'Field label for: Provider.',
			displayPath: 'History|Additional Agreement|Details|Provider',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.Provider',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldLabelReviewState: {
			defaultValue: 'Review Status',
			description: 'Field label for: Review State.',
			displayPath: 'History|Additional Agreement|Details|ReviewState',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.ReviewState',
		},
		CPCReqHistoryAdditionalAgreementDetailsFieldLabelUpdated: {
			defaultValue: 'Updated',
			description: 'Field label for: Updated.',
			displayPath: 'History|Additional Agreement|Details|Updated',
			portal: 'CP',
			displayType: 'Label',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName: 'OtherRequirement/History.AdditionalAgreement.Details.FieldLabel.Updated',
		},

		CPCReqHistoryAdditionalAgreementDetailsNotificationMessageUpdateSuccess: {
			defaultValue: 'Update successful',
			description: 'Notification message for successful update.',
			displayPath: 'History|Additional Agreement|Details|UpdateSuccess',
			portal: 'CP',
			displayType: 'Notification',
			feature: HubFeature.OtherRequirement,
			htmlAllowed: false,
			shared: false,
			legacyName:
				'OtherRequirement/History.AdditionalAgreement.Details.NotificationMessage.UpdateSuccess',
		},
		...creqAdditionalAgreementSharedLocs,
	};

export const creqAdditionalAgreementDetailPageLocs: Pick<
	LocMap<CPCReqHistoryAdditionalAgreementDetailLocNames>,
	| 'CPCReqHistoryAdditionalAgreementDetailsPageTitle'
	| 'CPCReqHistoryAdditionalAgreementDetailsPageDescription'
> = {
	CPCReqHistoryAdditionalAgreementDetailsPageTitle:
		'CPCReqHistoryAdditionalAgreementDetailsPageTitle',
	CPCReqHistoryAdditionalAgreementDetailsPageDescription:
		'CPCReqHistoryAdditionalAgreementDetailsPageDescription',
};

export const cpCreqAdditionalAgreementDetailLocKeys =
	MapLocKeys<CPCReqHistoryAdditionalAgreementDetailLocNames>(creqAdditionalAgreementDetailLocs);

export type CPCreqHistoryAdditionalAgreementDetailLocKeys =
	typeof cpCreqAdditionalAgreementDetailLocKeys;
